// Educational content for calculator selections

export interface CareerStageInfo {
  id: string;
  name: string;
  description: string;
  artistTypes: string[];
  venueTypes: string[];
  campaignTypes: string[];
  idealFor: string;
  examples: string[];
}

export interface ShowCountInfo {
  range: string;
  description: string;
  benefits: string[];
  considerations: string[];
}

export const CAREER_STAGE_OVERVIEW = `
At MAX, we understand that artists at different career stages offer unique opportunities for brand partnerships. Our career stage classification helps brands identify the right artist tier for their campaign objectives, audience reach goals, and budget considerations.

Each career stage represents a different level of audience size, venue capacity, market reach, and partnership complexity. Understanding these distinctions helps brands make informed decisions about their music marketing investments.
`;

export const CAREER_STAGES_DATA: Record<string, CareerStageInfo> = {
  'Emerging': {
    id: 'emerging',
    name: 'Emerging',
    description: 'Up-and-coming artists building their first significant fanbase',
    artistTypes: [
      'New artists with growing social followings (10K-100K)',
      'Local/regional acts gaining momentum',
      'Artists with first major label deals or viral content',
      'Breakout artists from talent shows or streaming platforms'
    ],
    venueTypes: [
      'Small clubs and intimate venues (200-1,000 capacity)',
      'Local music venues and bars',
      'Festival side stages and emerging artist showcases',
      'College venues and university events'
    ],
    campaignTypes: [
      'Brand awareness with younger demographics',
      'Product launches targeting early adopters',
      'Local market activation and community building',
      'Authentic brand storytelling and grassroots marketing'
    ],
    idealFor: 'Brands seeking authentic connections with younger audiences and cost-effective local market penetration.',
    examples: ['Rising indie bands', 'Viral TikTok artists', 'Local music scene leaders', 'Competition winners']
  },
  'Established 1': {
    id: 'established-1',
    name: 'Established 1',
    description: 'Artists with proven track records and growing regional influence',
    artistTypes: [
      'Regional touring artists with dedicated fanbases',
      'Artists with charting singles or albums',
      'Performers with 100K-500K social media followers',
      'Acts with radio play and streaming success'
    ],
    venueTypes: [
      'Mid-size theaters and concert halls (1,000-3,000 capacity)',
      'Regional amphitheaters and outdoor venues',
      'Music festival main stages',
      'Multi-market touring venues'
    ],
    campaignTypes: [
      'Regional brand campaigns and market expansion',
      'Product endorsements and brand partnerships',
      'Cross-promotional campaigns with lifestyle brands',
      'Targeted demographic reach with authentic messaging'
    ],
    idealFor: 'Brands looking for proven audience engagement with moderate investment and regional market impact.',
    examples: ['Regional touring bands', 'Genre-specific chart artists', 'Streaming platform favorites', 'Festival regulars']
  },
  'Established 2': {
    id: 'established-2',
    name: 'Established 2',
    description: 'Mid-tier artists with national recognition and solid commercial success',
    artistTypes: [
      'National touring artists with multiple successful releases',
      'Artists with 500K-2M social media followers',
      'Grammy-nominated or award-winning performers',
      'Acts with consistent chart performance'
    ],
    venueTypes: [
      'Large theaters and arenas (3,000-8,000 capacity)',
      'National amphitheater circuits',
      'Major music festival headlining slots',
      'Premium venue partnerships'
    ],
    campaignTypes: [
      'National brand campaigns and product launches',
      'Lifestyle brand partnerships and endorsements',
      'Multi-channel marketing campaigns',
      'Premium product positioning and brand elevation'
    ],
    idealFor: 'Brands seeking national reach with established credibility and moderate-to-high investment levels.',
    examples: ['National touring artists', 'Chart-topping performers', 'Award winners', 'Major label artists']
  },
  'Established 3': {
    id: 'established-3',
    name: 'Established 3',
    description: 'Well-known artists with strong commercial appeal and broad market reach',
    artistTypes: [
      'Multi-platinum artists with global recognition',
      'Artists with 2M-10M social media followers',
      'Regular chart-toppers and award recipients',
      'Artists with significant cultural influence'
    ],
    venueTypes: [
      'Major arenas and amphitheaters (8,000-20,000 capacity)',
      'Stadium shows and large festival headlining',
      'Premium venue partnerships and residencies',
      'International touring venues'
    ],
    campaignTypes: [
      'Major brand campaigns and global launches',
      'High-profile endorsements and partnerships',
      'Cultural moment marketing and trend setting',
      'Premium brand association and lifestyle positioning'
    ],
    idealFor: 'Brands requiring significant cultural impact, broad demographic reach, and premium brand association.',
    examples: ['Multi-platinum artists', 'Major award winners', 'Cultural icons', 'Global touring acts']
  },
  'Mainstream 1': {
    id: 'mainstream-1',
    name: 'Mainstream 1',
    description: 'Major artists with massive audiences and cultural significance',
    artistTypes: [
      'A-list celebrities with global recognition',
      'Artists with 10M+ social media followers',
      'Chart-dominating performers with cultural impact',
      'Multi-generational appeal and broad demographic reach'
    ],
    venueTypes: [
      'Major stadiums and arenas (20,000+ capacity)',
      'World-class venues and iconic locations',
      'Major festival headlining and exclusive events',
      'Premium brand activation spaces'
    ],
    campaignTypes: [
      'Global brand campaigns and cultural moments',
      'Major product launches and brand repositioning',
      'Cultural partnership and social impact campaigns',
      'Premium brand elevation and market leadership'
    ],
    idealFor: 'Brands seeking maximum cultural impact, global reach, and association with cultural leaders.',
    examples: ['Grammy winners', 'Global superstars', 'Cultural icons', 'Household names']
  },
  'Mainstream 2': {
    id: 'mainstream-2',
    name: 'Mainstream 2',
    description: 'Superstar artists with unprecedented reach and cultural dominance',
    artistTypes: [
      'Global superstars with massive cultural influence',
      'Artists with 25M+ social media followers',
      'Chart-record holders and industry leaders',
      'Multi-platform entertainment personalities'
    ],
    venueTypes: [
      'Largest stadiums and iconic venues worldwide',
      'Exclusive venues and custom event spaces',
      'Major festival closers and special events',
      'Historic and landmark venue partnerships'
    ],
    campaignTypes: [
      'Landmark brand campaigns and cultural shifts',
      'Global product ecosystems and brand universes',
      'Social movement marketing and cultural leadership',
      'Premium brand transformation and market dominance'
    ],
    idealFor: 'Brands requiring unprecedented cultural impact, global dominance, and transformational market presence.',
    examples: ['Global icons', 'Cultural phenomena', 'Industry legends', 'Transcendent artists']
  },
  'Mainstream 3': {
    id: 'mainstream-3',
    name: 'Mainstream 3',
    description: 'Legendary artists with generational impact and timeless appeal',
    artistTypes: [
      'Legendary performers with decades of success',
      'Artists with 50M+ total audience reach',
      'Hall of Fame caliber performers',
      'Multi-generational cultural icons'
    ],
    venueTypes: [
      'World-renowned venues and cultural landmarks',
      'Exclusive luxury venues and private events',
      'Historic performance spaces and special occasions',
      'Custom venue transformations and unique experiences'
    ],
    campaignTypes: [
      'Legacy brand campaigns and heritage marketing',
      'Luxury brand partnerships and exclusive experiences',
      'Cultural institution collaborations',
      'Premium lifestyle and aspirational marketing'
    ],
    idealFor: 'Luxury brands and heritage companies seeking association with timeless cultural significance.',
    examples: ['Hall of Fame artists', 'Cultural legends', 'Timeless performers', 'Generational icons']
  },
  'Mainstream 4': {
    id: 'mainstream-4',
    name: 'Mainstream 4',
    description: 'Iconic artists representing the pinnacle of cultural achievement',
    artistTypes: [
      'Once-in-a-generation cultural phenomenons',
      'Artists with unprecedented global influence',
      'Cultural architects and society shapers',
      'Transcendent performers beyond traditional metrics'
    ],
    venueTypes: [
      'Iconic global venues and cultural monuments',
      'Exclusive invitation-only experiences',
      'Historic and transformational spaces',
      'Custom luxury venue creations'
    ],
    campaignTypes: [
      'Transformational cultural campaigns',
      'Global brand evolution and market redefinition',
      'Cultural milestone marketing',
      'Premium brand transcendence and legacy building'
    ],
    idealFor: 'Elite brands seeking to redefine markets and create lasting cultural impact.',
    examples: ['Cultural phenomena', 'Society definers', 'Transcendent artists', 'Once-in-a-lifetime performers']
  }
};

export const SHOW_COUNT_OVERVIEW = `
The number of shows in your sponsorship campaign directly impacts both cost efficiency and campaign effectiveness. At MAX, we've learned that asking an artist to modify their run of show (RoS) for just one performance is the most expensive option - you're asking them to change their established routine for a single event.

However, when you sponsor multiple shows, you benefit from economies of scale. Artists and their teams can amortize the setup costs, rehearsal time, and production changes across multiple performances, resulting in lower per-show costs and better integration of your brand into the overall tour experience.
`;

export const SHOW_COUNT_DATA: Record<string, ShowCountInfo> = {
  '1': {
    range: '1 Show',
    description: 'Single show sponsorship with maximum flexibility but highest per-show cost',
    benefits: [
      'Test a partnership with minimal commitment',
      'Target specific markets or demographics',
      'Perfect for product launches or special events',
      'Complete creative control over single activation'
    ],
    considerations: [
      'Highest cost per show due to one-time setup',
      'Artist must modify established run of show',
      'Limited economies of scale',
      'Requires full setup for single performance'
    ]
  },
  '2-4': {
    range: '2-4 Shows',
    description: 'Small tour sponsorship with moderate cost savings and campaign continuity',
    benefits: [
      'Beginning of cost efficiencies',
      'Campaign message reinforcement',
      'Multiple market testing opportunities',
      'Improved artist team familiarity with activation'
    ],
    considerations: [
      'Still relatively high per-show costs',
      'Limited tour integration opportunities',
      'Moderate setup amortization',
      'Beginning of partnership momentum'
    ]
  },
  '5-9': {
    range: '5-9 Shows',
    description: 'Medium tour sponsorship with notable cost reductions and campaign momentum',
    benefits: [
      'Significant per-show cost reductions',
      'Strong campaign narrative development',
      'Enhanced artist partnership integration',
      'Multiple demographic and geographic targets'
    ],
    considerations: [
      'Moderate investment commitment required',
      'Campaign coordination complexity increases',
      'Need for consistent activation quality',
      'Regional market coverage opportunities'
    ]
  },
  '10-19': {
    range: '10-19 Shows',
    description: 'Large tour sponsorship with substantial savings and comprehensive campaign reach',
    benefits: [
      'Major per-show cost efficiencies',
      'Comprehensive tour integration',
      'National or multi-regional reach',
      'Strong artist partnership development',
      'Enhanced content creation opportunities'
    ],
    considerations: [
      'Significant investment commitment',
      'Complex logistics and coordination',
      'Need for scalable activation strategies',
      'Long-term partnership management'
    ]
  },
  '20+': {
    range: '20+ Shows',
    description: 'Full tour partnership with maximum cost efficiency and cultural impact',
    benefits: [
      'Maximum cost per show efficiency',
      'Complete tour integration and co-branding',
      'National or international reach',
      'Deep artist partnership and collaboration',
      'Comprehensive content and media opportunities',
      'Cultural moment creation potential'
    ],
    considerations: [
      'Major investment and commitment required',
      'Complex multi-market coordination',
      'Long-term relationship management',
      'Need for sophisticated activation strategies',
      'Significant planning and execution timeline'
    ]
  }
};

export function getCareerStageInfo(stage: string): CareerStageInfo | null {
  return CAREER_STAGES_DATA[stage] || null;
}

export function getShowCountInfo(count: number): ShowCountInfo {
  if (count === 1) return SHOW_COUNT_DATA['1'];
  if (count <= 4) return SHOW_COUNT_DATA['2-4'];
  if (count <= 9) return SHOW_COUNT_DATA['5-9'];
  if (count <= 19) return SHOW_COUNT_DATA['10-19'];
  return SHOW_COUNT_DATA['20+'];
}